/**
 * Карточка Mini Apps — окно с свернуть/закрыть (02_GPT-MODULAR).
 */

import { useState, useEffect, useRef } from "react";
import Typewriter from "../Typewriter/Typewriter";
import IconAndroid from "../../assets/icons/IconAndroid";
import IconFlutter from "../../assets/icons/IconFlutter";
import { SERVICE_MINI_APPS } from "../../config/services";
import "./MiniAppsCard.css";

function MiniAppsCard() {
    const [miniAppsPlankClosing, setMiniAppsPlankClosing] = useState(false);
    const [miniAppsPlankClosed, setMiniAppsPlankClosed] = useState(false);
    const [miniAppsPlankOpening, setMiniAppsPlankOpening] = useState(false);
    const [miniAppsMinimized, setMiniAppsMinimized] = useState(false);
    const [closingCardHeight, setClosingCardHeight] = useState(null);
    const miniAppsCardRef = useRef(null);
    const closingTargetHeightRef = useRef(null);
    const fullCardHeightRef = useRef(null);
    const handleMiniAppsMinimize = () => {
        // Если идёт анимация закрытия/открытия - подождём
        if (miniAppsPlankClosing || miniAppsPlankOpening) return;
        
        // Сбрасываем inline height если есть, чтобы не конфликтовал с transform
        if (closingCardHeight !== null) {
            setClosingCardHeight(null);
        }
        
        // Переключаем минимизацию (работает и для закрытого окна)
        setMiniAppsMinimized(prev => !prev);
    };

    const handleMiniAppsClose = () => {
        // Если уже анимируется - ничего не делаем
        if (miniAppsPlankClosing || miniAppsPlankOpening) return;
        
        const card = miniAppsCardRef.current;
        
        // Если закрыто - открываем
        if (miniAppsPlankClosed) {
            if (card) {
                const currentHeight = card.offsetHeight; // Высота бара
                setClosingCardHeight(currentHeight);
                closingTargetHeightRef.current = fullCardHeightRef.current || 300; // Полная высота из ref
            }
            setMiniAppsPlankClosed(false);
            setMiniAppsPlankOpening(true);
            return;
        }
        
        // Если открыто - закрываем (остаётся минимизированным если было минимизировано)
        if (card) {
            const bar = card.querySelector(".miniapps-plank-bar");
            fullCardHeightRef.current = card.offsetHeight; // Сохраняем полную высоту для последующего открытия
            closingTargetHeightRef.current = bar ? bar.offsetHeight : 52;
            setClosingCardHeight(card.offsetHeight);
        }
        setMiniAppsPlankClosing(true);
    };

    // Анимация закрытия
    useEffect(() => {
        if (!miniAppsPlankClosing) return;
        const raf = requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (closingTargetHeightRef.current != null) {
                    setClosingCardHeight(closingTargetHeightRef.current);
                }
            });
        });
        const t = setTimeout(() => {
            setMiniAppsPlankClosed(true);
            setMiniAppsPlankClosing(false);
        }, 950);
        return () => {
            cancelAnimationFrame(raf);
            clearTimeout(t);
        };
    }, [miniAppsPlankClosing]);

    // Анимация открытия
    useEffect(() => {
        if (!miniAppsPlankOpening) return;
        const raf = requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (closingTargetHeightRef.current != null) {
                    setClosingCardHeight(closingTargetHeightRef.current); // Плавно до полной высоты
                }
            });
        });
        const t = setTimeout(() => {
            setMiniAppsPlankOpening(false);
            setClosingCardHeight(null); // После анимации убираем inline height
        }, 950);
        return () => {
            cancelAnimationFrame(raf);
            clearTimeout(t);
        };
    }, [miniAppsPlankOpening]);

    return (
                    <article
                        ref={miniAppsCardRef}
                        className={`group-9__card group-9__card_green group-9__card_win${miniAppsPlankClosing || miniAppsPlankClosed ? " group-9__card_win_closing" : ""}${miniAppsPlankClosed ? " group-9__card_win_closed" : ""}${miniAppsPlankOpening ? " group-9__card_win_opening" : ""}${miniAppsMinimized ? " group-9__card_win_minimized" : ""}`}
                        style={
                            closingCardHeight !== null
                                ? { height: `${closingCardHeight}px`, overflow: "hidden" }
                                : undefined
                        }
                    >
                        {/* Верхняя плашка — всегда видна */}
                        <div className="miniapps-plank-bar">
                            <div className="group-9__win-titlebar">
                                <span className="group-9__win-title">Mini Apps</span>
                                <div className="group-9__win-controls" aria-hidden="true">
                                    <span className="group-9__win-btn" title="Minimize" />
                                    <span
                                        role="button"
                                        tabIndex={0}
                                        className="group-9__win-btn"
                                        title="Maximize"
                                        aria-label="Свернуть/развернуть"
                                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleMiniAppsMinimize(); }}
                                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleMiniAppsMinimize(); } }}
                                    />
                                    <span
                                        role="button"
                                        tabIndex={0}
                                        className="group-9__win-btn group-9__win-btn-close"
                                        title="Close"
                                        aria-label="Закрыть"
                                        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleMiniAppsClose(); }}
                                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleMiniAppsClose(); }}
                                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleMiniAppsClose(); } }}
                                    />
                                </div>
                            </div>
                        </div>
                        {/* Нижняя часть — обрезается при анимации высоты карточки */}
                        <div
                            className={`miniapps-plank-body${miniAppsPlankClosed ? " miniapps-plank-body_collapsed" : ""}`}
                        >
                            <div className="miniapps-plank-body__grid-cell">
                            <div className="group-9__card-terminal-body">
                                <div className="group-9__icons group-9__icons_black" aria-hidden="true">
                                <span className="group-9__icon-wrap" style={{overflow: 'visible'}}>
                                    <IconAndroid />
                                </span>
                                <span className="group-9__icon-wrap">
                                    <IconFlutter />
                                </span>
                            </div>
                            <h3 className="group-9__title">
                                <Typewriter text={SERVICE_MINI_APPS.title} speed={SERVICE_MINI_APPS.titleSpeed} delay={SERVICE_MINI_APPS.titleDelay} />
                            </h3>
                            <p className="group-9__text">
                                <Typewriter
                                    text={SERVICE_MINI_APPS.text}
                                    speed={SERVICE_MINI_APPS.textSpeed}
                                    delay={SERVICE_MINI_APPS.textDelay}
                                />
                            </p>
                            </div>
                            </div>
                        </div>
                    </article>

    );
}

export default MiniAppsCard;
// components/MiniAppsCard/MiniAppsCard.js
